import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Input, Spin, Badge, message, Modal } from 'antd';
import { LeftOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import api from '../api';
import { getStoredUser, isSuperAdmin } from '../userAuth';

const typeMap = { 1: '借用申请', 2: '变更申请', 3: '处置申请', 4: '处置撤回申请' };
const typeColorMap = {
  1: { color: '#1677ff', bg: '#e6f4ff' },
  2: { color: '#52c41a', bg: '#f0fff4' },
  3: { color: '#fa541c', bg: '#fff2e8' },
  4: { color: '#722ed1', bg: '#f9f0ff' },
};
const statusMap = { 0: '待审核', 1: '已通过', 2: '已驳回' };
const statusColorMap = {
  0: { color: '#fa8c16', bg: '#fff7e6' },
  1: { color: '#52c41a', bg: '#f0fff4' },
  2: { color: '#f5222d', bg: '#fff1f0' },
};
const filterTabsAll = [
  { key: 0, label: '全部' },
  { key: 1, label: '借用申请' },
  { key: 2, label: '变更申请' },
  { key: 3, label: '处置申请' },
  { key: 4, label: '处置撤回申请' },
];
const filterTabsRegionMgr = [
  { key: 0, label: '全部' },
  { key: 1, label: '借用申请' },
  { key: 2, label: '变更申请' },
];

function hiddenKey(userId) {
  return `yc_approve_hidden_${userId || '0'}`;
}

function loadHiddenIds(userId) {
  try {
    const raw = localStorage.getItem(hiddenKey(userId));
    const arr = JSON.parse(raw || '[]');
    return new Set(Array.isArray(arr) ? arr.map(Number).filter((n) => n > 0) : []);
  } catch {
    return new Set();
  }
}

function saveHiddenIds(userId, set) {
  try {
    localStorage.setItem(hiddenKey(userId), JSON.stringify([...set]));
  } catch {
    /* ignore */
  }
}

export default function Approve() {
  const navigate = useNavigate();
  const user = getStoredUser();
  const canApproveScrap = isSuperAdmin(user);
  const filterTabs = canApproveScrap ? filterTabsAll : filterTabsRegionMgr;
  const [tab, setTab] = useState('pending');
  const [typeFilter, setTypeFilter] = useState(0);
  const [pendingList, setPendingList] = useState([]);
  const [doneList, setDoneList] = useState([]);
  const [hiddenIds, setHiddenIds] = useState(() => loadHiddenIds(user?.id));
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);
  const [rejectModal, setRejectModal] = useState(null);
  const [remarkText, setRemarkText] = useState('');
  const [openDeleteId, setOpenDeleteId] = useState(null);
  const [draggingId, setDraggingId] = useState(null);
  const [dragOffset, setDragOffset] = useState(0);
  const touchStartXRef = useRef(0);

  const fetchPending = useCallback(() => {
    return api.get('/applies/pending')
      .then(res => setPendingList(res?.items || []))
      .catch(() => {});
  }, []);

  const fetchDone = useCallback(() => {
    return api.get('/applies/approved-by-me')
      .then(res => setDoneList(res?.items || []))
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!canApproveScrap && (typeFilter === 3 || typeFilter === 4)) setTypeFilter(0);
  }, [canApproveScrap, typeFilter]);

  useEffect(() => {
    setLoading(true);
    if (tab === 'pending') {
      fetchPending().finally(() => setLoading(false));
    } else {
      fetchDone().finally(() => setLoading(false));
    }
  }, [tab, fetchPending, fetchDone]);

  const handleApprove = async (item) => {
    setActionLoading(item.id);
    try {
      await api.put(`/applies/${item.id}/approve`, { action: 'approve', remark: '' });
      message.success('已通过');
      fetchPending();
    } catch (err) {
      message.error(err.message || '操作失败');
    } finally {
      setActionLoading(null);
    }
  };

  const handleReject = async () => {
    if (!rejectModal) return;
    setActionLoading(rejectModal.item.id);
    try {
      await api.put(`/applies/${rejectModal.item.id}/approve`, { action: 'reject', remark: remarkText });
      message.success('已驳回');
      setRejectModal(null);
      setRemarkText('');
      fetchPending();
    } catch (err) {
      message.error(err.message || '操作失败');
    } finally {
      setActionLoading(null);
    }
  };

  const hideRecords = useCallback((ids) => {
    setHiddenIds((prev) => {
      const next = new Set(prev);
      ids.forEach((id) => next.add(Number(id)));
      saveHiddenIds(user?.id, next);
      return next;
    });
    setOpenDeleteId(null);
  }, [user?.id]);

  const handleSwipeDelete = (item) => {
    hideRecords([item.id]);
    message.success('已删除');
  };

  const deleteAllDone = () => {
    const visible = doneList.filter((item) => !hiddenIds.has(Number(item.id)));
    if (!visible.length) {
      message.info('暂无可删除的已审核记录');
      return;
    }
    Modal.confirm({
      title: '确认删除',
      content: `将删除 ${visible.length} 条已审核记录（仅从本机列表移除，不影响业务数据）`,
      okText: '删除',
      cancelText: '取消',
      okButtonProps: { danger: true },
      onOk: () => {
        hideRecords(visible.map((i) => i.id));
        message.success('已删除全部已审核记录');
      },
    });
  };

  const markAllRead = async () => {
    try {
      const res = await api.put('/messages/read-all', {}, { params: { msg_type: 2 } });
      const n = Number(res?.updated || 0);
      message.success(n > 0 ? `审批消息已全部已读（${n}条）` : '没有未读审批消息');
    } catch (err) {
      message.error(err.message || '操作失败');
    }
  };

  const handleTouchStart = (e, itemId) => {
    if (tab !== 'done') return;
    touchStartXRef.current = e.touches?.[0]?.clientX || 0;
    setDraggingId(itemId);
    setDragOffset(openDeleteId === itemId ? -72 : 0);
  };

  const handleTouchMove = (e, itemId) => {
    if (tab !== 'done' || draggingId !== itemId) return;
    const currentX = e.touches?.[0]?.clientX || 0;
    const dx = currentX - touchStartXRef.current;
    const base = openDeleteId === itemId ? -72 : 0;
    setDragOffset(Math.max(-72, Math.min(0, base + dx)));
  };

  const handleTouchEnd = (itemId) => {
    if (tab !== 'done' || draggingId !== itemId) return;
    setOpenDeleteId(dragOffset <= -36 ? itemId : null);
    setDraggingId(null);
    setDragOffset(0);
  };

  const list = tab === 'pending' ? pendingList : doneList;
  const filtered = useMemo(() => {
    const base = typeFilter === 0 ? list : list.filter((item) => item.apply_type === typeFilter);
    if (tab !== 'done') return base;
    return base.filter((item) => !hiddenIds.has(Number(item.id)));
  }, [list, typeFilter, tab, hiddenIds]);

  const renderAssetInfo = (item) => {
    const asset = item.assets?.[0];
    if (!asset) return null;
    return (
      <div style={{ background: '#f5f7fa', borderRadius: 8, padding: '10px 12px', marginBottom: 10 }}>
        <div style={{ fontSize: 14, fontWeight: 500, color: '#333' }}>{asset.asset_name}</div>
        <div style={{ fontSize: 12, color: '#999', marginTop: 2 }}>{asset.asset_code}</div>
      </div>
    );
  };

  const renderChangeDetail = (item) => {
    if (item.apply_type !== 2) return null;
    return (
      <div style={{ fontSize: 13, color: '#666', marginBottom: 8 }}>
        {item.old_user_name && item.new_user_name && (
          <div style={{ marginBottom: 4 }}>
            使用人: {item.old_user_name} <span style={{ color: '#1677ff', margin: '0 4px' }}>&rarr;</span> {item.new_user_name}
          </div>
        )}
        {item.old_location_name && item.new_location_name && (
          <div>
            存放位置: {item.old_location_name} <span style={{ color: '#1677ff', margin: '0 4px' }}>&rarr;</span> {item.new_location_name}
          </div>
        )}
      </div>
    );
  };

  const renderScrapDetail = (item) => {
    if (item.apply_type !== 3) return null;
    const asset = item.assets?.[0];
    return (
      <div style={{ fontSize: 13, color: '#666', marginBottom: 8 }}>
        {item.remark && <div style={{ marginBottom: 4 }}>处置原因: {item.remark}</div>}
        {asset?.net_value != null && <div>当前净值: ¥{Number(asset.net_value).toLocaleString()}</div>}
      </div>
    );
  };

  const renderCardInner = (item) => {
    const tc = typeColorMap[item.apply_type] || { color: '#999', bg: '#f5f5f5' };
    const sc = statusColorMap[item.status] || statusColorMap[0];
    const applicant = item.apply_user?.username || item.apply_user_name || '-';
    const asset = item.assets?.[0];

    return (
      <div style={{ background: '#fff', borderRadius: 10, padding: 14 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: 12, color: tc.color, background: tc.bg, padding: '2px 8px', borderRadius: 4, fontWeight: 500 }}>
              {typeMap[item.apply_type] || '未知'}
            </span>
            <span style={{ fontSize: 12, color: '#999' }}>
              {dayjs(item.create_time).format('YYYY-MM-DD HH:mm')}
            </span>
          </div>
          <span style={{ fontSize: 12, color: sc.color, background: sc.bg, padding: '2px 8px', borderRadius: 4 }}>
            {statusMap[item.status] || '待审核'}
          </span>
        </div>
        <div style={{ fontSize: 15, fontWeight: 500, color: '#333', marginBottom: 10 }}>
          {applicant} 申请{typeMap[item.apply_type]?.replace('申请', '') || ''} {asset?.asset_name || ''}
        </div>
        {renderAssetInfo(item)}
        {renderChangeDetail(item)}
        {renderScrapDetail(item)}
        {tab === 'pending' && (
          <div style={{ display: 'flex', gap: 10, marginTop: 4 }}>
            <Button
              block
              onClick={() => { setRejectModal({ item }); setRemarkText(''); }}
              loading={actionLoading === item.id}
              style={{ borderRadius: 8, height: 38 }}
            >
              驳回
            </Button>
            <Button
              type="primary"
              block
              onClick={() => handleApprove(item)}
              loading={actionLoading === item.id}
              style={{ borderRadius: 8, height: 38 }}
            >
              同意
            </Button>
          </div>
        )}
      </div>
    );
  };

  const renderCard = (item) => {
    if (tab !== 'done') {
      return <div key={item.id} style={{ marginBottom: 10 }}>{renderCardInner(item)}</div>;
    }
    const offset = draggingId === item.id ? dragOffset : (openDeleteId === item.id ? -72 : 0);
    return (
      <div key={item.id} style={{ position: 'relative', marginBottom: 10, borderRadius: 10, overflow: 'hidden' }}>
        <div
          style={{
            position: 'absolute', right: 0, top: 0, bottom: 0, width: 72,
            background: '#ff4d4f', color: '#fff', display: 'flex', alignItems: 'center',
            justifyContent: 'center', fontSize: 14, fontWeight: 600,
          }}
          onClick={() => handleSwipeDelete(item)}
        >
          删除
        </div>
        <div
          onTouchStart={(e) => handleTouchStart(e, item.id)}
          onTouchMove={(e) => handleTouchMove(e, item.id)}
          onTouchEnd={() => handleTouchEnd(item.id)}
          style={{
            transform: `translateX(${offset}px)`,
            transition: draggingId === item.id ? 'none' : 'transform 0.18s ease',
            position: 'relative',
            zIndex: 1,
          }}
        >
          {renderCardInner(item)}
        </div>
      </div>
    );
  };

  return (
    <div style={{ minHeight: '100vh', background: '#f5f7fa', paddingBottom: 70 }}>
      <div style={{ display: 'flex', alignItems: 'center', padding: '12px 16px', background: '#fff', borderBottom: '1px solid #f0f0f0', position: 'sticky', top: 0, zIndex: 10 }}>
        <LeftOutlined onClick={() => navigate(-1)} style={{ fontSize: 18, marginRight: 12 }} />
        <span style={{ fontSize: 17, fontWeight: 600, flex: 1 }}>申请审核</span>
      </div>

      <div style={{ display: 'flex', background: '#fff', borderBottom: '1px solid #f0f0f0' }}>
        {[
          { key: 'pending', label: '待审核', count: pendingList.length },
          { key: 'done', label: '已审核' },
        ].map(t => (
          <div
            key={t.key}
            onClick={() => { setTab(t.key); setTypeFilter(0); setOpenDeleteId(null); }}
            style={{
              flex: 1, textAlign: 'center', padding: '12px 0', fontSize: 15, fontWeight: 500, cursor: 'pointer',
              color: tab === t.key ? '#1677ff' : '#666',
              borderBottom: tab === t.key ? '2px solid #1677ff' : '2px solid transparent',
            }}
          >
            {t.count != null && t.count > 0 ? (
              <Badge count={t.count} size="small" offset={[6, -2]}><span>{t.label}</span></Badge>
            ) : t.label}
          </div>
        ))}
      </div>

      <div style={{ display: 'flex', background: '#fff', padding: '8px 16px', overflowX: 'auto', borderBottom: '1px solid #f0f0f0' }}>
        {filterTabs.map(ft => (
          <div
            key={ft.key}
            onClick={() => setTypeFilter(ft.key)}
            style={{
              padding: '4px 14px', borderRadius: 16, fontSize: 13, cursor: 'pointer', whiteSpace: 'nowrap',
              color: typeFilter === ft.key ? '#fff' : '#666',
              background: typeFilter === ft.key ? '#1677ff' : '#f5f7fa',
              marginRight: 8,
            }}
          >
            {ft.label}
          </div>
        ))}
      </div>

      <div style={{ textAlign: 'right', padding: '10px 16px 4px' }}>
        {tab === 'done' && (
          <span onClick={deleteAllDone} style={{ fontSize: 14, color: '#1677ff', cursor: 'pointer', marginRight: 14 }}>
            删除全部已读
          </span>
        )}
        <span onClick={markAllRead} style={{ fontSize: 14, color: '#1677ff', cursor: 'pointer' }}>
          全部已读
        </span>
      </div>

      <div style={{ padding: '4px 16px 12px' }}>
        <Spin spinning={loading}>
          {filtered.length === 0 && !loading ? (
            <div style={{ textAlign: 'center', color: '#999', padding: 60 }}>
              {tab === 'pending' ? '暂无待审核记录' : '暂无已审核记录'}
            </div>
          ) : (
            filtered.map(item => renderCard(item))
          )}
        </Spin>
        {tab === 'done' && filtered.length > 0 ? (
          <div style={{ textAlign: 'center', fontSize: 12, color: '#bbb', padding: '8px 0' }}>
            左滑记录可删除
          </div>
        ) : null}
      </div>

      {rejectModal && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100 }}>
          <div style={{ background: '#fff', borderRadius: 12, padding: '24px 20px', width: 300 }}>
            <div style={{ fontSize: 16, fontWeight: 600, marginBottom: 12 }}>确认驳回</div>
            <div style={{ fontSize: 14, color: '#666', marginBottom: 12 }}>确认驳回该申请？</div>
            <Input.TextArea
              rows={3}
              placeholder="请输入驳回原因（选填）"
              value={remarkText}
              onChange={e => setRemarkText(e.target.value)}
              style={{ borderRadius: 8, marginBottom: 16 }}
            />
            <div style={{ display: 'flex', gap: 10 }}>
              <Button block style={{ borderRadius: 8 }} onClick={() => { setRejectModal(null); setRemarkText(''); }}>取消</Button>
              <Button type="primary" danger block style={{ borderRadius: 8 }} loading={!!actionLoading} onClick={handleReject}>确认驳回</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
